import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Percent, Plus, Pencil, ToggleLeft, ToggleRight, Info, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_TAX_TYPES = [
  { id: 'vat-20', name: 'Normalsatz',          rate: 20, category: 'A', country: 'AT', enabled: true,  description: 'Alkohol, Tabak, sonstige Waren' },
  { id: 'vat-13', name: 'Zwischensatz',         rate: 13, category: 'B', country: 'AT', enabled: true,  description: 'Wein ab Hof, Blumen, Kunstgegenstände' },
  { id: 'vat-10', name: 'Ermäßigter Satz',      rate: 10, category: 'C', country: 'AT', enabled: true,  description: 'Speisen, Softdrinks, Bücher, Medikamente' },
  { id: 'vat-0',  name: 'Nullsatz',             rate:  0, category: 'D', country: 'AT', enabled: true,  description: 'Steuerfreie Umsätze (§ 6 UStG)' },
  { id: 'vat-19', name: 'Normalsatz (DE)',       rate: 19, category: 'A', country: 'DE', enabled: false, description: 'Deutschland — Regelsteuersatz' },
  { id: 'vat-7',  name: 'Ermäßigter Satz (DE)', rate:  7, category: 'B', country: 'DE', enabled: false, description: 'Deutschland — ermäßigter Satz (Lebensmittel)' },
];

const COUNTRY_FLAGS = { AT: '🇦🇹', DE: '🇩🇪' };

export default function AdminTaxTypes() {
  const [taxTypes, setTaxTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', rate: '', category: 'A', country: 'AT', description: '' });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const settings = await getSettings();
      const saved = settings.tax_types;
      if (Array.isArray(saved) && saved.length > 0) {
        setTaxTypes(saved);
      } else {
        setTaxTypes(DEFAULT_TAX_TYPES);
      }
    } catch {
      toast.error('Fehler beim Laden');
      setTaxTypes(DEFAULT_TAX_TYPES);
    } finally {
      setIsLoading(false);
    }
  };

  const saveToDb = async (updated) => {
    setIsSaving(true);
    try {
      const settings = await getSettings();
      await updateSettings({ ...settings, tax_types: updated });
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTaxType = async (id) => {
    const updated = taxTypes.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t);
    setTaxTypes(updated);
    await saveToDb(updated);
    toast.success('Steuertyp aktualisiert');
  };

  const openDialog = (type = null) => {
    setEditing(type);
    setForm(type
      ? { name: type.name, rate: String(type.rate), category: type.category, country: type.country, description: type.description || '' }
      : { name: '', rate: '', category: 'A', country: 'AT', description: '' }
    );
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || form.rate === '') { toast.error('Bitte alle Felder ausfüllen'); return; }
    const rate = parseFloat(form.rate);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error('Steuersatz muss zwischen 0 und 100 liegen'); return; }

    let updated;
    if (editing) {
      updated = taxTypes.map(t => t.id === editing.id ? { ...t, ...form, rate } : t);
    } else {
      updated = [...taxTypes, { id: `vat-custom-${Date.now()}`, ...form, rate, enabled: true }];
    }
    setTaxTypes(updated);
    await saveToDb(updated);
    toast.success(editing ? 'Steuertyp aktualisiert' : 'Steuertyp erstellt');
    setShowDialog(false);
  };

  const enabledCount = taxTypes.filter(t => t.enabled).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Steuertypen</h1>
          <p className="text-slate-500">{enabledCount} von {taxTypes.length} aktiv · RKSV Steuerkategorien</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadSettings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Steuertyp
          </Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-0.5">Österreich RKSV — Steuerkategorien A–D</p>
          <p className="text-amber-600">Jede Transaktion muss einer Steuerkategorie (A/B/C/D/E) zugeordnet werden. Diese werden im Jahresbeleg an das Finanzamt übermittelt. Änderungen nur nach Rücksprache mit dem Steuerberater.</p>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Bezeichnung</TableHead>
                <TableHead className="text-center">Satz</TableHead>
                <TableHead className="text-center">Kategorie</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead className="text-center">Land</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {taxTypes.map((type) => (
                <TableRow key={type.id} className={type.enabled ? '' : 'opacity-50'}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        type.enabled ? 'bg-emerald-50' : 'bg-slate-100'
                      }`}>
                        <Percent className={`w-4 h-4 ${type.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <span className="font-medium">{type.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-bold tabular-nums text-lg">{type.rate}%</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="px-2 py-1 bg-slate-100 rounded font-mono text-sm font-bold">
                      {type.category}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{type.description}</TableCell>
                  <TableCell className="text-center text-xl">{COUNTRY_FLAGS[type.country] || type.country}</TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={() => toggleTaxType(type.id)}
                      disabled={isSaving}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        type.enabled
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {type.enabled
                        ? <><ToggleRight className="w-3.5 h-3.5" /> Aktiv</>
                        : <><ToggleLeft className="w-3.5 h-3.5" /> Inaktiv</>
                      }
                    </button>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openDialog(type)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Steuertyp bearbeiten' : 'Neuer Steuertyp'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Bezeichnung *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Ermäßigter Satz" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Steuersatz (%) *</Label>
                <Input type="number" min={0} max={100} step={0.1} value={form.rate}
                  onChange={e => setForm({ ...form, rate: e.target.value })} placeholder="z.B. 10" />
              </div>
              <div>
                <Label>RKSV-Kategorie</Label>
                <div className="flex gap-2 mt-1">
                  {['A','B','C','D','E'].map(cat => (
                    <button key={cat} type="button"
                      onClick={() => setForm({ ...form, category: cat })}
                      className={`flex-1 py-2 rounded-lg border text-sm font-bold transition-colors ${
                        form.category === cat
                          ? 'bg-[#1a2f38] text-white border-[#1a2f38]'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label>Land</Label>
              <div className="flex gap-2 mt-1">
                {[['AT','🇦🇹 Österreich'],['DE','🇩🇪 Deutschland']].map(([code, label]) => (
                  <button key={code} type="button"
                    onClick={() => setForm({ ...form, country: code })}
                    className={`flex-1 py-2 rounded-lg border text-sm transition-colors ${
                      form.country === code
                        ? 'bg-[#1a2f38] text-white border-[#1a2f38]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="z.B. Speisen, Softdrinks, Bücher" />
            </div>
            <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[#1a2f38]">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
